import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import apiUrl from "../Apiurl";
import { BlockSkeleton, TextSkeleton } from "../interfaccia/loading";
import CronStatusRow from "./CronStatusRow";
import ForceCronDialog from "./ForceCronDialog";
import {
  formatDateTime,
  formatRelativeTime,
  isProblemSeverity,
  severityConfig,
  sortCronJobs,
} from "./cronStatus.utils";
import "./CronStatusDashboard.css";

const DEFAULT_POLL_INTERVAL_MS = 30_000;
const ACTIVE_POLL_INTERVAL_MS = 5_000;
const CONNECTOR_JOB_KEY = "checkConnectorAhAvailability";

function connectorStatusLabel(job) {
  if (!job) return "Stato sconosciuto";
  if (job.enabled === false || job.severity === "disabled") return "Controllo disattivato";
  if (job.severity === "healthy") return "Disponibile";
  if (job.severity === "running") return "Controllo in corso";
  if (job.severity === "error") return "Non disponibile";
  if (job.severity === "stalled") return "Controllo bloccato";
  if (job.severity === "stale") return "Controllo in ritardo";
  if (job.severity === "warning") return "Da verificare";
  return "Stato sconosciuto";
}

function ConnectorCheckItem({ label, check, successLabel, errorLabel, detail }) {
  const ok = check?.ok;
  const icon = ok === true ? "check_circle" : ok === false ? "error" : "help_outline";
  const colorClass = ok === true ? "dark-green" : ok === false ? "dark-red" : "gray";
  const value = ok === true
    ? successLabel
    : ok === false
      ? errorLabel
      : "Non verificabile";

  return (
    <div className="cron-connector-check pa3">
      <div className="flex items-center">
        <span className={`material-icons mr2 ${colorClass}`} aria-hidden="true">{icon}</span>
        <div>
          <div className="f7 scrittaColMedia">{label}</div>
          <div className={`b mt1 ${colorClass}`}>{value}</div>
        </div>
      </div>
      {detail ? <div className="f7 scrittaColMedia mt2 break-word">{detail}</div> : null}
    </div>
  );
}

function ConnectorAvailabilityCard({ job }) {
  const severity = severityConfig(job?.severity || "unknown");
  const observedAt = job?.activeRun?.startedAt || job?.lastRunAt;
  const health = job?.connectorHealth?.checks;
  const connectorCheck = health?.connector || {
    ok: job?.severity === "healthy"
      ? true
      : job?.severity === "error"
        ? false
        : null,
  };
  const missingEnvironment = health?.environment?.missing || [];
  const environmentDetail = missingEnvironment.length
    ? `Mancano: ${missingEnvironment.join(", ")}`
    : null;
  const modaDuration = Number(health?.modaDatabase?.durationMs);
  const modaDetail = health?.modaDatabase?.error || (
    Number.isFinite(modaDuration) ? `Risposta in ${modaDuration} ms` : null
  );

  return (
    <section
      className="cron-connector-card sfondoBiancoTema br3 ba bordoColAlt mb4"
      aria-label="Stato connettore Domino-Moda"
      role="status"
    >
      <div className="cron-connector-card-main pa3 pa4-ns">
        <div className={`cron-connector-card-icon ${severity.colorClass}`}>
          <span className="material-icons" aria-hidden="true">{severity.icon}</span>
        </div>
        <div className="cron-connector-card-copy">
          <div className="f7 tracked ttu scrittaColMedia">Integrazione prenotazioni Moda</div>
          <div className="f4 b mt1">Connettore Domino-Moda</div>
          <div className="f7 scrittaColMedia mt2">
            Monitorato 24/7 · controllo ogni 30 minuti · allarme massimo una volta al giorno
          </div>
          {job?.headline ? <div className="f7 mt2 break-word">{job.headline}</div> : null}
        </div>
        <div className="cron-connector-card-state">
          <div className={`f5 b ${severity.colorClass}`}>
            {connectorStatusLabel(job)}
          </div>
          <div className="f7 scrittaColMedia mt1" title={formatDateTime(observedAt)}>
            {job?.activeRun
              ? `Avviato ${formatRelativeTime(observedAt)}`
              : `Ultimo controllo ${formatRelativeTime(observedAt)}`}
          </div>
          <div className="f7 scrittaColMedia mt1">{job?.status || "N/D"}</div>
        </div>
      </div>
      <div className="cron-connector-checks bt bordoColAlt">
        <ConnectorCheckItem
          label="Connettore"
          check={connectorCheck}
          successLabel="Attivo"
          errorLabel="Non raggiungibile"
          detail={connectorCheck?.error}
        />
        <ConnectorCheckItem
          label="Variabili necessarie"
          check={health?.environment}
          successLabel="Complete"
          errorLabel="Incomplete"
          detail={environmentDetail}
        />
        <ConnectorCheckItem
          label="Database Moda"
          check={health?.modaDatabase}
          successLabel="Connesso"
          errorLabel="Non raggiungibile"
          detail={modaDetail}
        />
      </div>
    </section>
  );
}

function DashboardSkeleton() {
  return (
    <div aria-label="Caricamento monitor processi">
      <BlockSkeleton tone="soft" height={190} className="mb4" />
      <div className="cron-summary-grid mb4">
        {[0, 1, 2, 3, 4, 5].map((item) => (
          <BlockSkeleton key={item} tone="soft" height={88} />
        ))}
      </div>
      {[0, 1, 2, 3, 4].map((item) => (
        <div key={item} className="sfondoBiancoTema br3 ba pa3 mb2">
          <BlockSkeleton tone="soft" width="34%" height={18} className="mb2" />
          <TextSkeleton tone="soft" lines={2} lineHeight={12} />
        </div>
      ))}
    </div>
  );
}

function DashboardDAM({ utente }) {
  const { getAccessTokenSilently } = useAuth0();
  const [monitor, setMonitor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [search, setSearch] = useState("");
  const [groupFilter, setGroupFilter] = useState("");
  const [severityFilter, setSeverityFilter] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [expandedKey, setExpandedKey] = useState(null);
  const [details, setDetails] = useState({});
  const [detailLoading, setDetailLoading] = useState({});
  const [detailErrors, setDetailErrors] = useState({});
  const [forceJob, setForceJob] = useState(null);
  const [forceBusy, setForceBusy] = useState(false);
  const [forceError, setForceError] = useState("");
  const requestSequence = useRef(0);

  const authorizedRequest = useCallback(async (path, options = {}) => {
    const accessToken = await getAccessTokenSilently();
    const response = await fetch(`${String(apiUrl).replace(/\/$/, "")}${path}`, {
      ...options,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        ...(options.headers || {}),
      },
    });
    const body = await response.json().catch(() => null);
    if (!response.ok) {
      throw new Error(body?.error || response.statusText || "Richiesta non riuscita");
    }
    return body?.data;
  }, [getAccessTokenSilently]);

  const loadMonitor = useCallback(async (mode = "load") => {
    const requestId = ++requestSequence.current;
    if (mode === "refresh") setRefreshing(true);
    else setLoading(true);
    setError("");
    try {
      const data = await authorizedRequest("/manutenzione/cron-monitor");
      if (requestId === requestSequence.current) setMonitor(data);
    } catch (loadError) {
      if (requestId === requestSequence.current) {
        setError(loadError.message || "Impossibile caricare il monitor processi");
      }
    } finally {
      if (requestId === requestSequence.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [authorizedRequest]);

  useEffect(() => {
    loadMonitor();
  }, [loadMonitor]);

  const hasRunningJobs = Boolean(
    monitor?.jobs?.some((job) => job.activeRun),
  );

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const interval = window.setInterval(() => {
      if (document.visibilityState === "visible") loadMonitor("refresh");
    }, hasRunningJobs ? ACTIVE_POLL_INTERVAL_MS : DEFAULT_POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [autoRefresh, hasRunningJobs, loadMonitor]);

  const toggleDetail = async (jobKey) => {
    if (expandedKey === jobKey) {
      setExpandedKey(null);
      return;
    }
    setExpandedKey(jobKey);
    if (details[jobKey] || detailLoading[jobKey]) return;
    setDetailLoading((current) => ({ ...current, [jobKey]: true }));
    setDetailErrors((current) => ({ ...current, [jobKey]: "" }));
    try {
      const detail = await authorizedRequest(`/manutenzione/cron-monitor/${encodeURIComponent(jobKey)}`);
      setDetails((current) => ({ ...current, [jobKey]: detail }));
    } catch (detailError) {
      setDetailErrors((current) => ({
        ...current,
        [jobKey]: detailError.message || "Impossibile caricare il dettaglio",
      }));
    } finally {
      setDetailLoading((current) => ({ ...current, [jobKey]: false }));
    }
  };

  const requestRun = (job) => {
    setForceError("");
    setForceJob(job);
  };

  const confirmRun = async () => {
    if (!forceJob) return;
    const username = utente?.username;
    if (!username) {
      setForceError("Username Domino non disponibile per l'audit.");
      return;
    }
    setForceBusy(true);
    setForceError("");
    try {
      await authorizedRequest(
        `/manutenzione/cron-monitor/${encodeURIComponent(forceJob.key)}/run`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Domino-Username": username,
          },
          body: JSON.stringify({ username }),
        },
      );
      setNotice(`${forceJob.label} avviato.`);
      setForceJob(null);
      setDetails((current) => ({ ...current, [forceJob.key]: undefined }));
      await loadMonitor("refresh");
    } catch (runError) {
      setForceError(runError.message || "Impossibile avviare il processo");
    } finally {
      setForceBusy(false);
    }
  };

  const groups = useMemo(
    () => Array.from(new Set((monitor?.jobs || []).map((job) => job.group))).sort(),
    [monitor],
  );

  const connectorJob = useMemo(
    () => monitor?.jobs?.find((job) => job.key === CONNECTOR_JOB_KEY) || null,
    [monitor],
  );

  const visibleJobs = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return sortCronJobs(monitor?.jobs || []).filter((job) => {
      if (groupFilter && job.group !== groupFilter) return false;
      if (severityFilter === "problems" && !isProblemSeverity(job.severity)) return false;
      if (severityFilter && severityFilter !== "problems" && job.severity !== severityFilter) return false;
      if (!normalizedSearch) return true;
      return [job.key, job.label, job.group, job.headline, job.status]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(normalizedSearch);
    });
  }, [groupFilter, monitor, search, severityFilter]);

  const summaryCards = [
    {
      key: "problems",
      label: "Problemi",
      icon: "error",
      colorClass: "dark-red",
      value:
        Number(monitor?.summary?.error || 0) +
        Number(monitor?.summary?.stalled || 0) +
        Number(monitor?.summary?.warning || 0) +
        Number(monitor?.summary?.stale || 0) +
        Number(monitor?.summary?.unknown || 0),
    },
    { key: "stalled", ...severityConfig("stalled"), label: "Bloccati", value: monitor?.summary?.stalled || 0 },
    { key: "stale", ...severityConfig("stale"), label: "In ritardo", value: monitor?.summary?.stale || 0 },
    { key: "running", ...severityConfig("running"), label: "In esecuzione", value: monitor?.summary?.running || 0 },
    { key: "healthy", ...severityConfig("healthy"), label: "Regolari", value: monitor?.summary?.healthy || 0 },
    { key: "disabled", ...severityConfig("disabled"), label: "Disattivati", value: monitor?.summary?.disabled || 0 },
  ];


  return (
    <div className="colonnaTuttaPagina-ns pa2 pa4-ns">
      <div className="cron-monitor-shell">
        <div className="flex flex-wrap justify-between items-center mb4">
          <div>
            <h2 className="ma0">Monitor processi</h2>
            <p className="mt2 mb0 scrittaColMedia">
              Esito dei cron, ritardi e azioni operative disponibili.
            </p>
          </div>
          <div className="flex items-center mt3 mt0-ns">
            <span className="f7 scrittaColMedia mr3">
              {monitor?.generatedAt
                ? `Aggiornato ${new Date(monitor.generatedAt).toLocaleTimeString("it-IT")}`
                : "Non aggiornato"}
            </span>
            <button
              type="button"
              className="button-reset br2 pa2 sfondoBiancoTema pointer"
              onClick={() => loadMonitor("refresh")}
              disabled={loading || refreshing}
              title="Aggiorna monitor"
            >
              <span className="material-icons v-mid" aria-hidden="true">refresh</span>
              <span className="ml2">{refreshing ? "Aggiorno..." : "Aggiorna"}</span>
            </button>
          </div>
        </div>

        {notice ? (
          <div className="pa3 mb3 br2 ba dark-green" role="status">
            {notice}
          </div>
        ) : null}
        {error ? (
          <div className="pa3 mb3 br2 ba dark-red" role="alert">
            {error}
          </div>
        ) : null}

        {loading && !monitor ? <DashboardSkeleton /> : (
          <>
            <ConnectorAvailabilityCard job={connectorJob} />

            <div className="cron-summary-grid mb4">
              {summaryCards.map((card) => (
                <button
                  key={card.key}
                  type="button"
                  className="cron-summary-card button-reset sfondoBiancoTema br3 pa3 pointer"
                  onClick={() => setSeverityFilter(
                    severityFilter === card.key ? "" : card.key,
                  )}
                  aria-pressed={severityFilter === card.key}
                >
                  <div className="flex justify-between items-center">
                    <span className="f6 scrittaColMedia">{card.label}</span>
                    <span className={`material-icons ${card.colorClass}`} aria-hidden="true">
                      {card.icon}
                    </span>
                  </div>
                  <div className={`f2 b mt2 ${card.colorClass}`}>{card.value}</div>
                </button>
              ))}
            </div>

            <div className="cron-toolbar sfondoBiancoTema br3 pa3 mb3">
              <label>
                <span className="db f7 mb1">Cerca</span>
                <input
                  type="search"
                  className="input-reset ba bordoColAlt br2 pa2 w-100 sfondoBiancoTema"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Processo, gruppo, messaggio..."
                />
              </label>
              <label>
                <span className="db f7 mb1">Gruppo</span>
                <select
                  className="ba bordoColAlt br2 pa2 w-100 sfondoBiancoTema"
                  value={groupFilter}
                  onChange={(event) => setGroupFilter(event.target.value)}
                >
                  <option value="">Tutti</option>
                  {groups.map((group) => <option key={group} value={group}>{group}</option>)}
                </select>
              </label>
              <label>
                <span className="db f7 mb1">Stato</span>
                <select
                  className="ba bordoColAlt br2 pa2 w-100 sfondoBiancoTema"
                  value={severityFilter}
                  onChange={(event) => setSeverityFilter(event.target.value)}
                >
                  <option value="">Tutti</option>
                  <option value="problems">Solo problemi</option>
                  <option value="error">Errori</option>
                  <option value="stalled">Potenzialmente bloccati</option>
                  <option value="stale">In ritardo</option>
                  <option value="warning">Attenzione</option>
                  <option value="running">In esecuzione</option>
                  <option value="healthy">Regolari</option>
                  <option value="disabled">Disattivati</option>
                  <option value="unknown">Sconosciuti</option>
                </select>
              </label>
              <label className="flex items-center pb2 pointer">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(event) => setAutoRefresh(event.target.checked)}
                />
                <span className="ml2">Auto-refresh</span>
              </label>
            </div>

            <div className="mb2 f7 scrittaColMedia">
              {visibleJobs.length} processi visualizzati
            </div>
            {visibleJobs.length ? visibleJobs.map((job) => (
              <CronStatusRow
                key={job.key}
                job={job}
                expanded={expandedKey === job.key}
                detail={details[job.key]}
                detailLoading={Boolean(detailLoading[job.key])}
                detailError={detailErrors[job.key]}
                onToggle={toggleDetail}
                onRequestRun={requestRun}
              />
            )) : (
              <div className="sfondoBiancoTema br3 ba bordoColAlt pa4 tc scrittaColMedia">
                Nessun processo corrisponde ai filtri.
              </div>
            )}
          </>
        )}
      </div>

      <ForceCronDialog
        job={forceJob}
        busy={forceBusy}
        error={forceError}
        onCancel={() => !forceBusy && setForceJob(null)}
        onConfirm={confirmRun}
      />
    </div>
  );
}

export default DashboardDAM;
